import React, { useState } from 'react'
import { Plus, Trash2, Calculator, Save, Send } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { useTheme } from '@/contexts/ThemeContext'
import { ThemedCard } from '@/components/ui/ThemedCard'

interface JournalLine {
  id: string
  account_id: string
  account_code: string
  account_name: string
  debit: number
  credit: number
  description?: string
}

const ManualJournalPage = () => {
  const { theme } = useTheme()
  const [journalLines, setJournalLines] = useState<JournalLine[]>([
    { id: '1', account_id: '', account_code: '', account_name: '', debit: 0, credit: 0 }
  ])
  const [memo, setMemo] = useState('')

  const addLine = () => {
    setJournalLines([...journalLines, {
      id: Date.now().toString(),
      account_id: '',
      account_code: '',
      account_name: '',
      debit: 0,
      credit: 0
    }])
  }

  const removeLine = (id: string) => {
    if (journalLines.length > 1) {
      setJournalLines(journalLines.filter(line => line.id !== id))
    }
  }

  const updateLine = (id: string, field: keyof JournalLine, value: any) => {
    setJournalLines(lines =>
      lines.map(line =>
        line.id === id ? { ...line, [field]: value } : line
      )
    )
  }

  const calculateTotals = () => {
    const totalDebits = journalLines.reduce((sum, line) => sum + (line.debit || 0), 0)
    const totalCredits = journalLines.reduce((sum, line) => sum + (line.credit || 0), 0)
    return { totalDebits, totalCredits, difference: Math.abs(totalDebits - totalCredits) }
  }

  const { totalDebits, totalCredits, difference } = calculateTotals()
  const balanced = difference < 0.01

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>Manual Journal Entry</h2>
          <p style={{ color: theme.colors.textSecondary }}>Create adjusting and correcting entries</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-opacity hover:opacity-80"
            style={{
              backgroundColor: theme.colors.bgHover,
              color: theme.colors.textSecondary
            }}
          >
            <Calculator className="h-4 w-4" />
            Calculate
          </button>
          <button 
            className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-md transition-colors"
            style={{ backgroundColor: theme.colors.primaryColor }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryHoverColor}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.colors.primaryColor}
          >
            <Save className="h-4 w-4" />
            Save Draft
          </button>
          <button
            disabled={!balanced}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: balanced ? theme.colors.successText : theme.colors.bgHover,
              color: balanced ? 'white' : theme.colors.textTertiary
            }}
          >
            <Send className="h-4 w-4" />
            Post Entry
          </button>
        </div>
      </div>

      {/* Journal Form */}
      <ThemedCard padding="p-6">
        <div className="mb-6">
          <Label htmlFor="memo">Journal Memo</Label>
          <Textarea
            id="memo"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Enter journal description..."
            className="mt-1"
            rows={2}
          />
        </div>

        {/* Journal Lines */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold" style={{ color: theme.colors.textPrimary }}>Journal Lines</h3>
            <button
              onClick={addLine}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-opacity hover:opacity-80"
              style={{
                backgroundColor: theme.colors.bgHover,
                color: theme.colors.textSecondary
              }}
            >
              <Plus className="h-4 w-4" />
              Add Line
            </button>
          </div>

          {/* Lines Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ backgroundColor: theme.colors.bgHover }}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                    Account
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                    Description
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                    Debit
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                    Credit
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider" style={{ color: theme.colors.textSecondary }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.colors.borderColor }}>
                {journalLines.map((line) => (
                  <tr 
                    key={line.id}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.bgHover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td className="px-4 py-3">
                      <Select
                        value={line.account_id}
                        onValueChange={(value) => {
                          // Mock account lookup
                          const mockAccount = {
                            'acc1': { code: '1001', name: 'Cash - Operating' },
                            'acc2': { code: '4001', name: 'Service Revenue' },
                            'acc3': { code: '6001', name: 'Wages Expense' }
                          }[value] || { code: '', name: '' }

                          updateLine(line.id, 'account_id', value)
                          updateLine(line.id, 'account_code', mockAccount.code)
                          updateLine(line.id, 'account_name', mockAccount.name)
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="acc1">1001 - Cash - Operating</SelectItem>
                          <SelectItem value="acc2">4001 - Service Revenue</SelectItem>
                          <SelectItem value="acc3">6001 - Wages Expense</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={line.description || ''}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        placeholder="Line description..."
                        className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          borderColor: theme.colors.borderColor,
                          color: theme.colors.textPrimary
                        }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        value={line.debit || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0
                          updateLine(line.id, 'debit', value)
                          updateLine(line.id, 'credit', 0) // Clear credit when debit is entered
                        }}
                        placeholder="0.00"
                        className="w-full border rounded-md px-3 py-2 text-sm text-right focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          borderColor: theme.colors.borderColor,
                          color: theme.colors.textPrimary
                        }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        value={line.credit || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0
                          updateLine(line.id, 'credit', value)
                          updateLine(line.id, 'debit', 0) // Clear debit when credit is entered
                        }}
                        placeholder="0.00"
                        className="w-full border rounded-md px-3 py-2 text-sm text-right focus:outline-none focus:ring-2"
                        style={{
                          backgroundColor: theme.colors.bgPage,
                          borderColor: theme.colors.borderColor,
                          color: theme.colors.textPrimary
                        }}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => removeLine(line.id)}
                        disabled={journalLines.length === 1}
                        className="transition-opacity disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80"
                        style={{ color: theme.colors.textTertiary }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-6 pt-4 border-t" style={{ borderColor: theme.colors.borderColor }}>
            <div className="flex items-center justify-end gap-8">
              <div className="text-right">
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Total Debits</div>
                <div className="text-lg font-bold" style={{ color: '#fb923c' }}>
                  ${totalDebits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Total Credits</div>
                <div className="text-lg font-bold" style={{ color: '#60a5fa' }}>
                  ${totalCredits.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>Difference</div>
                <div className="text-lg font-bold" style={{ color: balanced ? theme.colors.successText : theme.colors.dangerText }}>
                  ${difference.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {!balanced && (
              <div className="mt-3 text-center">
                <span 
                  className="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                  style={{ backgroundColor: theme.colors.dangerBg, color: theme.colors.dangerText, borderColor: theme.colors.dangerBorder }}
                >
                  Journal must balance (debits = credits) before posting
                </span>
              </div>
            )}
          </div>
        </div>
      </ThemedCard>
    </div>
  )
}

export default ManualJournalPage
